import { Bloc, Websocket } from "@kernel/frontend-react-app";
import { RoomState, defaultRoomState } from "./RoomState";
import { RoomBloc } from "./RoomBloc";
import { StreamingHandler } from "./webrtc";
import {
  SignalingMessageChangedUsername,
  SignalingMessageType,
  SignalingService
} from "./services";
import { NotificationHandlerProps } from "../main";
import { LocalParticipant } from "@domain";
import { UniqueEntityId } from "@kernel/ddd";
import { ChangeParticipantDataInteractor } from "./interactors";
import { UserType } from "./UserType";

export type UserTypeFilterConfig = { types: UserType[] } | null;

export type RoomStreamingBlocState = Pick<
  RoomState,
  "room" | "statsRecorder"
> & {
  userTypeFilter: UserTypeFilterConfig;
};

export class RoomStreamingBloc extends Bloc<RoomStreamingBlocState> {
  private config: RTCConfiguration | null = null;
  private signaling: SignalingService | null = null;
  public constructor(private parent: RoomBloc) {
    super({
      room: defaultRoomState.room,
      statsRecorder: defaultRoomState.statsRecorder,
      userTypeFilter: null
    });
  }

  public handleNotification(props: NotificationHandlerProps): void {
    this.parent.handleNotification(props);
  }

  public setConfig(config: RTCConfiguration): void {
    this.config = config;
  }

  public getConfig(): RTCConfiguration {
    if (this.config === null) throw new Error("Config is null");
    return this.config;
  }

  public getSignaling(): SignalingService {
    if (this.signaling === null) throw new Error("Signaling is null");
    return this.signaling;
  }

  public setUserTypeFilter(config: UserTypeFilterConfig): void {
    this.changeState({
      ...this.state,
      userTypeFilter: config
    });
  }

  public getUserTypeFilter(): UserTypeFilterConfig {
    return this.state.userTypeFilter;
  }

  public async subscribeSignaling(ws: Websocket) {
    if (this.state.room === null) throw new Error("Room is null");
    if (this.state.room.localParticipant === null)
      throw new Error("Local participant is null");
    const config = this.getConfig();
    this.signaling = new SignalingService(ws);
    this.getSignaling().addEventListener(
      SignalingMessageType.CHANGE_USERNAME,
      this.changeUsername.bind(this)
    );
    StreamingHandler.create(this, this.signaling, config);
  }

  public changeUsername(e: unknown) {
    const event = e as CustomEvent<SignalingMessageChangedUsername>;
    if (this.state.room === null) throw new Error("Room is null");
    const room = this.state.room;
    if (!event.detail || typeof event.detail.data?.room !== "string") return;
    if (!room.id.equals(new UniqueEntityId(event.detail.data.room))) return;
    const participant = room.getRemoteParticipant(event.detail.data.user.id);
    if (!participant) return;
    participant.displayName = event.detail.data.user.username;
    this.changeState({
      ...this.state,
      room
    });
  }

  public changeLocalParticipant(displayName: string) {
    const changeLocalParticipant =
      ChangeParticipantDataInteractor.create<LocalParticipant>();
    const room = this.state.room;
    if (room === null) return;
    if (room.localParticipant === null) return;
    const participant = changeLocalParticipant.execute({
      participant: room.localParticipant,
      displayName
    });
    room.localParticipant = participant;
    if (participant.remoteId === undefined) return;
    this.getSignaling().changeUsername(
      room.id,
      participant.displayName,
      participant.remoteId
    );
    this.changeState({
      ...this.state,
      room
    });
  }

  public hangup() {
    const room = this.state.room;
    if (room === null) return;
    const participant = room.localParticipant;
    if (participant === null) return;
    if (participant.remoteId === undefined) return;
    this.getSignaling().deleteConnection(room.id, participant);
  }

  public async replaceScreenTrack(mediaStream: MediaStream): Promise<void> {
    await this.parent.replaceScreenTrack(mediaStream);
  }

  public static create(parent: RoomBloc): RoomStreamingBloc {
    const bloc = new RoomStreamingBloc(parent);
    bloc.subscribe((state: RoomStreamingBlocState) => {
      parent.changeState({
        ...parent.state,
        room: state.room
      });
    });
    return bloc;
  }
}
